import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductNotFoundStyles {
    listDashStyle?: InjectableCss;
    pageTitle?: TypographyPresentationProps;
    searchText?: InjectableCss;
    searchTerm?: InjectableCss;
    horizontalLine?: InjectableCss;
    cantFind?: InjectableCss;
    msgCall?: InjectableCss;
    msgCallText?: InjectableCss;
    pageNotFoundWrapper?: TypographyPresentationProps;
    goHomeButton?: ButtonPresentationProps;
    container?: InjectableCss;
}

export const productNotFoundStyles: ProductNotFoundStyles = {
    listDashStyle: {
        css: css`
            list-style-type: none;
            font-size: 16px;
            margin-left: 10px;
            margin-bottom: 15px;
            & li {
                text-indent: -5px;
                &:before {
                    content: "-";
                    text-indent: -5px;
                }
            }
        `,
    },
    pageTitle: {
        color: "text.accent",
        css: css`
            font-size: 20px;
        `,
    },
    searchText: {
        css: css`
            font-size: 16px;
        `,
    },
    searchTerm: {
        css: css`
            color: #ef3742;
        `,
    },
    horizontalLine: {
        css: css`
            height: 5.5px;
            border: none;
            background-color: lightgray;
        `,
    },
    cantFind: {
        css: css`
            padding: 5px 15px;
            background: #a81010;
            color: #fff;
            font-size: 28px;
            font-weight: 600;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: 4vw;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    msgCall: {
        css: css`
            background: #808080;
            color: #fff;
            font-size: 20px;
            padding: 10px 12px 5px 12px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: 2.85vw;
                        `,
                    ],
                    "max",
                )}
            & a.linkText {
                line-height: 1.2;
                span {
                    color: #fff;
                }
                &:hover {
                    span {
                        color: #fff;
                    }
                }
            }
        `,
    },
    msgCallText: {
        css: css`
            margin-right: 5px;
            line-height: 1.2;
        `,
    },
    pageNotFoundWrapper: {
        color: "text.accent",
    },
    goHomeButton: {
        color: "primary",
        css: css`
            font-weight: bold;
            font-size: 15px;
        `,
    },
    container: {
        css: css`
            text-align: center;
            a {
                padding: 10px 0px;
            }
        `,
    },
};

const StyledHr = getStyledWrapper("hr");
const StyledUl = getStyledWrapper("ul");
const StyledLi = getStyledWrapper("li");
const StyledSpan = getStyledWrapper("span");

interface OwnProps {
    extendedStyles?: ProductNotFoundStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    // productsState: state.pages.productList.productsState,
    isLoading: state.pages.productList.isLoading,
    productsDataView: getProductListDataView(state),
    productFilters: state.pages.productList.productFilters,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const ProductNotFound: FC<Props> = ({ productsDataView, productFilters, extendedStyles }) => {
    if (!productsDataView.value) {
        return null;
    }

    const query = productFilters.query;
    const [styles] = useState(() => mergeToNew(productNotFoundStyles, extendedStyles));

    return (
        <StyledWrapper data-test-selector="productListNoneFound" {...styles.pageNotFoundWrapper}>
            <Typography variant="h1" {...styles.pageTitle}>
                {translate("SITE RESULTS")}
            </Typography>
            <Typography as="p" {...styles.searchText}>
                {translate("Sorry, no products were found matching your search criteria:  ")}
                <StyledSpan {...styles.searchTerm}>{query}</StyledSpan>
            </Typography>

            <StyledHr {...styles.horizontalLine} />
            <StyledWrapper {...styles.container}>
                <Link href="/">
                    <LazyImage src="/UserFiles/images/icons/no-product-found.png" />
                </Link>
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                <Link href="/">
                    <Button {...styles.goHomeButton}>Go Back To Home</Button>
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductNotFound);
